import { Button } from "@components/common/button"
import { DictType } from "@pma/types"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog"
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { DELETE_FOLDER_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { LucideTrash } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { useNavigate, useSearchParams } from "react-router-dom"

interface Props {
  folder: DictType
  isMenuView?: boolean
  setFolderItems?: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  foldersMeta?: { [id: number]: number }
  setIsShow: React.Dispatch<React.SetStateAction<boolean>>
  breadcrumbs: { [id: number]: any[] }
  setBreadcrumbs: React.Dispatch<React.SetStateAction<{ [id: number]: any[] }>>
  handleTreeItemFolderClick: (item: DictType, forceExpand: boolean) => void
  setFoldersMeta: React.Dispatch<React.SetStateAction<{ [id: number]: number }>>
  setRecentDeletedFolder: React.Dispatch<React.SetStateAction<number>>
  setActiveFolder?: React.Dispatch<React.SetStateAction<DictType>>
  refetchAllFolder?: () => void
}

type Folder = {
  id: number
  created_at: number
  name: string
  project_id?: number
  root?: boolean
  parent_id?: number
  deleted?: boolean
  deleted_at?: number | null
  all_parents_folders_id?: number[]
  folder_size?: number
  last_update?: number
  path_count?: number
  icon?: string | null
  _files_in_folder?: number
  _total_files?: number
  _share?: any[]
}

type FolderStructure = {
  [key: string]: Folder[]
}

type FolderMap = {
  [key: string]: number
}

const DeleteFolder = ({
  folder,
  isMenuView = false,
  setFolderItems,
  foldersMeta,
  setIsShow,
  breadcrumbs,
  setBreadcrumbs,
  handleTreeItemFolderClick,
  setFoldersMeta,
  setRecentDeletedFolder,
  setActiveFolder,
  refetchAllFolder = () => {},
}: Props) => {
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const deleteFolder = async () => {
    const response = await axios.delete(SERVER_URL + DELETE_FOLDER_ENDPOINT + `/${folder?.id}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    return response.data
  }

  function removeFolderById(data: FolderStructure, folderId: number): FolderStructure {
    const updatedData: FolderStructure = {}

    for (const key in data) {
      if (parseInt(key) === folderId) continue

      const filteredEntries = data[key].filter((folder) => folder.id !== folderId)

      if (filteredEntries.length > 0) {
        updatedData[key] = filteredEntries
      }
    }

    return updatedData
  }

  function removeKeyFromObject(data: FolderMap, keyToRemove: string): FolderMap {
    const { [keyToRemove]: _, ...rest } = data
    return rest
  }

  const deleteFolderMutation = useMutation({
    mutationKey: ["delete-folder", folder?.id],
    mutationFn: deleteFolder,
    onSuccess: (data) => {
      setRecentDeletedFolder(folder.id)
      const parent = breadcrumbs[folder.id][breadcrumbs[folder.id].length - 2]
      handleTreeItemFolderClick(parent, true)

      const updatedData = removeFolderById(breadcrumbs, folder.id)
      if (setBreadcrumbs) {
        setBreadcrumbs(updatedData)
      }

      if (setFoldersMeta && foldersMeta) {
        const updatedMap = removeKeyFromObject(foldersMeta, folder.id)
        setFoldersMeta(updatedMap)
      }

      if (setFolderItems && foldersMeta) {
        const parentFolderId = foldersMeta[folder.id]

        if (parentFolderId) {
          setFolderItems((prev) => ({
            ...prev,
            [parentFolderId]: prev[parentFolderId].filter((item) => item.id !== folder.id),
          }))
        }
      }

      // If the deleted folder is the active folder, switch to its parent
      if (setActiveFolder && setBreadcrumbs && foldersMeta) {
        const parentFolderId = foldersMeta[folder.id]
        if (parentFolderId && folder.id === data.activeFolderId) {
          const parentFolder = Object.values(data.folderItems as { [id: number]: DictType[] })
            .flat()
            .find((item) => item.id === parentFolderId && item.as === "folder")
          if (parentFolder) {
            setActiveFolder(parentFolder)
            setBreadcrumbs((prev) => {
              const newBreadcrumbs = { ...prev }
              newBreadcrumbs[parentFolderId] = newBreadcrumbs[folder.id].slice(0, -1)
              delete newBreadcrumbs[folder.id]
              return newBreadcrumbs
            })
          }
        }
      }

      refetchAllFolder()
      toast({
        title: translate("Success"),
        description: `${folder?.name} ${translate("has been deleted")}`,
        variant: "success",
      })

      setIsShow(false)
      newParams.set("revalidate", "projects")
      newParams.set("action", "delete")
      navigate(`?${newParams.toString()}`)
    },
    onError: (error) => {
      setIsShow(false)
      toast({
        title: translate("Error"),
        description: `${translate("Unable to delete")} ${folder?.name}`,
        variant: "error",
      })
    },
  })

  const handleClick = () => {
    setIsShow(true)
    return deleteFolderMutation.mutate()
  }

  if (folder?.isRoot) {
    // Prevent deletion of root folder
    return <></>
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger>
        {isMenuView ? (
          <div className='flex items-center gap-2'>
            <Button size={"icon"} className='h-6 w-7' variant={"ghost"}>
              <LucideTrash className='text-red-500' size={16} />
            </Button>
            <span className='text-sm'>{`${translate("Delete folder")}`}</span>
          </div>
        ) : (
          <Tooltip>
            <TooltipTrigger>
              <Button variant='ghost' size={"icon"} className='bg-slate-200/50'>
                <LucideTrash className='text-red-500' size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent className='bg-destructive'>{translate("Delete folder")}</TooltipContent>
          </Tooltip>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to delete")} {folder?.name}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This action cannot be undone")}.
            {translate("This will permanently delete the file")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{translate("Cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className='bg-destructive'
            disabled={deleteFolderMutation?.isPending}
            onClick={handleClick}
          >
            {translate("Continue")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteFolder
